import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../services/supabase';
import './Hero.css';

function extractYouTubeId(url) {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
}

const CollageBackground = ({ isActive }) => {
    // Images for the collage
    const collageImages = [
        "/Images/collage/WhatsApp Image 2026-01-31 at 2.14.10 PM (1).jpeg",
        "/Images/collage/WhatsApp Image 2026-01-31 at 2.14.10 PM.jpeg",
        "/Images/collage/WhatsApp Image 2026-01-31 at 2.14.11 PM (2).jpeg",
        "/Images/collage/WhatsApp Image 2026-06-15 at 2.02.05 PM (1).jpeg",
        "/Images/collage/baptism.jpeg",
        "/Images/collage/worship.jpeg",
        "/Images/collage/young adults.jpg",
    ];

    // Double array for seamless loop
    const row1 = [...collageImages.slice(0, 4), ...collageImages.slice(0, 4)];
    const row2 = [...collageImages.slice(3), ...collageImages.slice(0, 3), ...collageImages.slice(3), ...collageImages.slice(0, 3)];
    const row3 = [...collageImages.slice(1, 6), ...collageImages.slice(1, 6)];

    return (
        <div className={`hero-collage-bg ${isActive ? 'active' : ''}`}>
            <div className="collage-row">
                {row1.map((src, i) => <img key={`r1-${i}`} src={src} alt="Church life" />)}
            </div>
            <div className="collage-row reverse">
                {row2.map((src, i) => <img key={`r2-${i}`} src={src} alt="Church life" />)}
            </div>
            <div className="collage-row">
                {row3.map((src, i) => <img key={`r3-${i}`} src={src} alt="Church life" />)}
            </div>
        </div>
    );
};

const HeroTypewriter = ({ title, subtitle }) => {
    const [titleText, setTitleText] = useState('');
    const [subtitleText, setSubtitleText] = useState('');
    const [showSubtitleCursor, setShowSubtitleCursor] = useState(false);
    const [showTitleCursor, setShowTitleCursor] = useState(false);

    useEffect(() => {
        let isCancelled = false;

        const runCycle = async () => {
            while (!isCancelled) {
                // Initial wait
                setTitleText('');
                setSubtitleText('');
                setShowTitleCursor(true);
                setShowSubtitleCursor(false);
                await new Promise(r => setTimeout(r, 500));

                // Type Title
                for (let i = 1; i <= title.length; i++) {
                    if (isCancelled) return;
                    setTitleText(title.substring(0, i));
                    await new Promise(r => setTimeout(r, 120));
                }
                setShowTitleCursor(false);

                // Wait before Subtitle
                await new Promise(r => setTimeout(r, 160));

                // Type Subtitle
                setShowSubtitleCursor(true);
                for (let i = 1; i <= subtitle.length; i++) {
                    if (isCancelled) return;
                    setSubtitleText(subtitle.substring(0, i));
                    await new Promise(r => setTimeout(r, 100));
                }

                // Pause for 12 seconds
                await new Promise(r => setTimeout(r, 12000));

                // Delete Subtitle
                for (let i = subtitle.length - 1; i >= 0; i--) {
                    if (isCancelled) return;
                    setSubtitleText(subtitle.substring(0, i));
                    await new Promise(r => setTimeout(r, 30));
                }
                setShowSubtitleCursor(false);

                // Delete Title
                setShowTitleCursor(true);
                for (let i = title.length - 1; i >= 0; i--) {
                    if (isCancelled) return;
                    setTitleText(title.substring(0, i));
                    await new Promise(r => setTimeout(r, 30));
                }
                setShowTitleCursor(false);

                // Wait before next cycle
                await new Promise(r => setTimeout(r, 1000));
            }
        };

        runCycle();

        return () => {
            isCancelled = true;
        };
    }, [title, subtitle]);

    return (
        <div className="hero-collage-text-inner">
            {title && (
                <h1>
                    {titleText}
                    {showTitleCursor && <span className="typewriter-cursor blink">|</span>}
                </h1>
            )}
            {subtitle && (
                <p>
                    {subtitleText}
                    {showSubtitleCursor && <span className="typewriter-cursor blink">|</span>}
                </p>
            )}
        </div>
    );
};

const heroSlides = [
    {
        id: 1,
        isCollage: true,
        tag: null,
        title: 'Welcome to Hope Community Church',
        subtitle: 'To know Christ, Make him known and Change the world',
        buttons: []
    },
    {
        id: 2,
        youtubeId: 'WQx0IUsX1ik',
        backgroundColor: '#000000',
        tag: null,
        title: '',
        subtitle: '',
        buttonsPosition: 'bottom-right',
        buttons: [
            { href: 'https://youtu.be/WQx0IUsX1ik?si=GZpThcpBGY6CDA8e', label: 'Watch Now', icon: 'play', style: 'primary', external: true }
        ]
    }
];

const Hero = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [dynamicSlides, setDynamicSlides] = useState(heroSlides);

    useEffect(() => {
        const fetchSermon = async () => {
            try {
                const { data } = await supabase
                    .from('sermons')
                    .select('youtube_url')
                    .order('created_at', { ascending: false })
                    .limit(1);

                if (data && data.length > 0) {
                    const url = data[0].youtube_url;
                    const ytId = extractYouTubeId(url);
                    if (ytId) {
                        setDynamicSlides(prev => {
                            const newSlides = [...prev];
                            newSlides[1] = {
                                ...newSlides[1],
                                youtubeId: ytId,
                                buttons: [
                                    { href: url, label: 'Watch Now', icon: 'play', style: 'primary', external: true }
                                ]
                            };
                            return newSlides;
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching latest sermon:", error);
            }
        };
        fetchSermon();
    }, []);

    const goToSlide = useCallback((index) => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentSlide(index);
        setTimeout(() => setIsAnimating(false), 600);
    }, [isAnimating]);

    const nextSlide = useCallback(() => {
        goToSlide((currentSlide + 1) % dynamicSlides.length);
    }, [currentSlide, goToSlide, dynamicSlides.length]);

    const prevSlide = useCallback(() => {
        goToSlide((currentSlide - 1 + dynamicSlides.length) % dynamicSlides.length);
    }, [currentSlide, goToSlide, dynamicSlides.length]);

    // Autoplay removed as requested so users can manually click next
    useEffect(() => {
        // No auto-advance
    }, []);

    const slide = dynamicSlides[currentSlide];

    return (
        <section
            className={`hero hero-slider${slide.isCollage ? ' hero-collage-slide' : ''}${slide.isAnniversary ? ' hero-anniversary' : ''}`}
            style={{
                ...(slide.backgroundImage ? { backgroundImage: `url("${slide.backgroundImage}")` } : {}),
                ...(slide.backgroundSize ? { backgroundSize: slide.backgroundSize, backgroundRepeat: 'no-repeat', backgroundPosition: 'center' } : {}),
                ...(slide.backgroundColor ? { backgroundColor: slide.backgroundColor } : {})
            }}
        >
            {slide.isCollage && <CollageBackground isActive={true} />}

            {/* Auto-playing YouTube background for video slides */}
            {slide.youtubeId && (
                <div className="hero-video-background">
                    <iframe
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=0&controls=1&showinfo=0&rel=0&loop=1&playlist=${slide.youtubeId}`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
            )}

            {/* Dark overlay only for non-collage slides */}
            {!slide.isCollage && <div className="hero-overlay"></div>}

            {/* Gradient overlay for better text readability on clear backgrounds */}
            {slide.isCollage && (
                <div className="collage-left-gradient"></div>
            )}

            {/* Collage slide: typed text on the left */}
            {slide.isCollage && (slide.title || slide.subtitle) && (
                <div className="hero-collage-text-bar" key={`collage-bar-${currentSlide}`}>
                    <HeroTypewriter title={slide.title} subtitle={slide.subtitle} />
                </div>
            )}

            {/* Non-collage slide: centered content */}
            {!slide.isCollage && (
                <div className="container hero-content">
                    {slide.tag && (
                        <span className="hero-tag animate-fade-in-up" key={`tag-${currentSlide}`}>{slide.tag}</span>
                    )}
                    {slide.title && (
                        <h1 className="animate-fade-in-up" key={`title-${currentSlide}`}>{slide.title}</h1>
                    )}
                    {slide.subtitle && (
                        <p className="animate-fade-in-up delay-100" key={`subtitle-${currentSlide}`}>{slide.subtitle}</p>
                    )}
                    {slide.buttons && slide.buttons.length > 0 && !slide.buttonsPosition && (
                        <div className="hero-buttons animate-fade-in-up delay-200" key={`btns-${currentSlide}`}>
                            {slide.buttons.map((btn, i) =>
                                btn.external ? (
                                    <a key={i} href={btn.href} target="_blank" rel="noopener noreferrer"
                                        className={`btn btn-${btn.style}`} title={btn.label}>
                                        {btn.label}
                                        {btn.icon === 'play' && <PlayCircle size={20} style={{ marginLeft: '8px' }} />}
                                    </a>
                                ) : (
                                    <Link key={i} to={btn.to} className={`btn btn-${btn.style}`}>
                                        {btn.label}
                                        {btn.icon === 'arrow' && <ArrowRight size={20} style={{ marginLeft: '8px' }} />}
                                    </Link>
                                )
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Specially positioned buttons */}
            {slide.buttons && slide.buttons.length > 0 && slide.buttonsPosition === 'bottom-right' && (
                <div className="hero-buttons-bottom-right animate-fade-in-up delay-200" key={`btns-br-${currentSlide}`}>
                    {slide.buttons.map((btn, i) =>
                        btn.external ? (
                            <a key={i} href={btn.href} target="_blank" rel="noopener noreferrer"
                                className={`btn btn-${btn.style}`} title={btn.label}>
                                {btn.label}
                                {btn.icon === 'play' && <PlayCircle size={20} style={{ marginLeft: '8px' }} />}
                            </a>
                        ) : (
                            <Link key={i} to={btn.to} className={`btn btn-${btn.style}`}>
                                {btn.label}
                                {btn.icon === 'arrow' && <ArrowRight size={20} style={{ marginLeft: '8px' }} />}
                            </Link>
                        )
                    )}
                </div>
            )}

            {/* Slider controls */}
            <button className="hero-slider-btn hero-slider-prev" onClick={prevSlide} aria-label="Previous slide">
                <ChevronLeft size={28} />
            </button>
            <button className="hero-slider-btn hero-slider-next" onClick={nextSlide} aria-label="Next slide">
                <ChevronRight size={28} />
            </button>

            {/* Dots */}
            <div className="hero-dots">
                {dynamicSlides.map((_, i) => (
                    <button
                        key={i}
                        className={`hero-dot ${i === currentSlide ? 'active' : ''}`}
                        onClick={() => goToSlide(i)}
                        aria-label={`Go to slide ${i + 1}`}
                    />
                ))}
            </div>
        </section>
    );
};

export default Hero;
